/**
 * PublicStatusLinkCard — admin-only card on WO detail that mints, copies,
 * and rotates the tokenized `/wo/{token}` link tenants can visit without
 * logging in. Also shows view-count analytics so the office knows the
 * tenant is watching (or hasn't opened it yet).
 */
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Link as LinkIcon, Copy, ArrowClockwise, Trash, Eye } from "@phosphor-icons/react";

export default function PublicStatusLinkCard({ jobId, wo_number }) {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try { setStats((await api.get(`/jobs/${jobId}/public-status-stats`)).data); } catch (_) {}
  };
  useEffect(() => { load(); }, [jobId]);

  const mint = async (rotate = false) => {
    setBusy(true);
    try {
      const r = await api.post(`/jobs/${jobId}/public-status-link${rotate ? "?rotate=true" : ""}`);
      toast.success(rotate ? "Link rotated — old link is dead" : "Tenant status link created");
      await load();
      // Auto-copy for convenience
      const url = `${window.location.origin}${r.data.path}`;
      try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) {}
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not create link")); }
    setBusy(false);
  };

  const copy = async () => {
    if (!stats?.token) return;
    const url = `${window.location.origin}/wo/${stats.token}`;
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success("Link copied"); }
    catch (_) { toast.error("Copy failed — long-press to copy manually"); }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke this tenant link? They won't be able to see status anymore.")) return;
    setBusy(true);
    try { await api.delete(`/jobs/${jobId}/public-status-link`); toast.success("Link revoked"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Revoke failed")); }
    setBusy(false);
  };

  const url = stats?.token ? `${window.location.origin}/wo/${stats.token}` : "";
  const smsText = wo_number
    ? `Your work order #${wo_number} — track status here: ${url}`
    : `Track your work order status here: ${url}`;

  return (
    <div className="mt-4 rounded-md border-2 border-cyan-200 bg-cyan-50/40 p-3" data-testid="public-status-link-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <LinkIcon weight="bold" size={16} className="text-cyan-700"/>
          <div>
            <div className="text-xs font-black uppercase tracking-widest text-cyan-800">Tenant Status Link</div>
            <div className="text-[10px] text-slate-500">
              {stats?.has_link
                ? `${stats.view_count || 0} view${stats.view_count === 1 ? "" : "s"}${stats.last_viewed_at ? ` · last opened ${new Date(stats.last_viewed_at).toLocaleString()}` : " · not opened yet"}`
                : "No link yet — mint one to share with the tenant"}
            </div>
          </div>
        </div>
        {stats?.has_link ? (
          <div className="flex items-center gap-1 flex-wrap">
            <button type="button" onClick={copy} data-testid="copy-tenant-link-btn"
              className="h-8 px-3 bg-cyan-600 hover:bg-cyan-700 text-white text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
              <Copy weight="bold" size={11}/> {copied ? "Copied" : "Copy"}
            </button>
            <a href={`sms:?&body=${encodeURIComponent(smsText)}`} data-testid="sms-tenant-link-btn"
              className="h-8 px-3 bg-white border-2 border-cyan-300 text-cyan-700 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1">
              SMS
            </a>
            <button type="button" onClick={() => mint(true)} disabled={busy} data-testid="rotate-tenant-link-btn" title="Rotate the token — the old link stops working"
              className="h-8 px-2 bg-white border-2 border-slate-300 text-slate-600 text-[10px] font-black uppercase tracking-widest disabled:opacity-50">
              <ArrowClockwise weight="bold" size={11}/>
            </button>
            <button type="button" onClick={revoke} disabled={busy} data-testid="revoke-tenant-link-btn" title="Kill this link"
              className="h-8 px-2 bg-white border-2 border-red-200 text-red-600 text-[10px] font-black uppercase tracking-widest disabled:opacity-50">
              <Trash weight="bold" size={11}/>
            </button>
          </div>
        ) : (
          <button type="button" onClick={() => mint(false)} disabled={busy} data-testid="mint-tenant-link-btn"
            className="h-9 px-4 bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-black uppercase tracking-widest disabled:opacity-50 inline-flex items-center gap-1">
            <LinkIcon weight="bold" size={12}/> Create link
          </button>
        )}
      </div>
      {stats?.has_link && (
        <div className="mt-2 text-[11px] font-mono text-slate-600 bg-white/70 border border-cyan-200 rounded px-2 py-1 truncate flex items-center gap-1" data-testid="tenant-link-preview">
          <Eye weight="bold" size={11} className="text-slate-400 shrink-0"/>
          <span className="truncate">{url}</span>
        </div>
      )}
    </div>
  );
}
